import type {
  KgInspectorOverviewFieldView,
  KgInspectorOverviewView,
  KgInspectorTableOverviewFieldView,
  KgInspectorTableOverviewView,
  KgInspectorTableSourceRef,
} from "../../components/kg/canvas/inspector/types";
import type { KgTableKeyType } from "../../components/molecules/KgTableNodeCard/KgTableNodeCard.types";
import type {
  KgDataModelOverviewResponse,
  KgDataModelRelationshipsResponse,
  KgDataSourceReportsResponse,
  KgDataSourceTablesResponse,
  KgDataSourceUsedInResponse,
  KgNodeOverviewResponse,
  KgSchemaMetricsResponse,
  KgSchemaRelationshipsResponse,
  KgTableOverviewField,
  KgTableOverviewRefValue,
  KgTableOverviewResponse,
  KgTablePreviewRow,
  KgDataStoryActivityResponse,
  KgDataStoryOverviewResponse,
  KgArtifactActivityResponse,
  KgArtifactOverviewResponse,
  KgReportActivityResponse,
  KgUsedInSectionKey,
} from "./kgDrawerApi.types";
import type { KgFlowNodeType } from "../../components/kg/canvas/types";
import {
  isExternalHttpUrl,
  isInspectorFocusHref,
  normalizeDataSourceTableRow,
  normalizeMetricOntologyLink,
  normalizeOntologyDataSourceItem,
  normalizeOntologyOverviewRef,
  normalizeSchemaMetricItem,
  normalizeSchemaRelationshipRow,
  normalizeUsedInItem,
  toInspectorNavLink,
} from "./kgInspectorNavLink";
import { toReportCanvasNodeId } from "./kgNodeIds";
import { resolveConnectorIconUrl } from "../../utils/connectorIcons";
import { formatRelativeTime } from "../../utils/formatRelativeTime";
import type {
  KgInspectorMetricRow,
  KgInspectorOntologyLinkRow,
  KgInspectorMetricFormulaView,
  KgInspectorMetricOverviewView,
  KgInspectorDataStoryOverviewView,
  KgInspectorArtifactOverviewView,
  KgInspectorArtifactActivityView,
  KgInspectorReportActivityRow,
  KgInspectorReportOverviewView,
  KgInspectorOntologyOverviewView,
  KgInspectorOntologyReportRow,
  KgInspectorRelationshipPairRow,
  KgInspectorRelationshipRow,
  KgInspectorReportRow,
  KgInspectorRuleSection,
  KgInspectorTableRow,
  KgInspectorUsedInSection,
} from "../../components/kg/canvas/inspector/types";
import type {
  KgOntologyLinkEntityRef,
  KgOntologyLinksResponse,
  KgOntologyLinkRow,
  KgOntologyOverviewField,
  KgOntologyOverviewRefValue,
  KgOntologyOverviewResponse,
  KgOntologyReportApiRow,
  KgOntologyReportRow,
  KgMetricFormulaResponse,
  KgMetricOverviewResponse,
  KgOntologyReportsResponse,
  KgOntologyRulesResponse,
  KgReportOverviewResponse,
  KgReportTraceResponse,
  KgTraceSectionKey,
} from "./kgDrawerApi.types";

const USED_IN_SECTION_NODE_TYPE: Record<KgUsedInSectionKey, KgFlowNodeType> = {
  ontologies: "ontology",
  stories: "dataStory",
  dataStories: "dataStory",
  reports: "report",
  /** @deprecated API key — sections labeled Reports should use `reports`. */
  metrics: "report",
};

const usedInSectionId = (key: KgUsedInSectionKey): string => {
  if (key === "ontologies") return "ontologies";
  if (key === "dataStories" || key === "stories") return "dataStories";
  if (key === "reports" || key === "metrics") return "reports";
  return key;
};

const TRACE_SECTION_NODE_TYPE: Record<KgTraceSectionKey, KgFlowNodeType> = {
  dataSources: "dataSource",
  tables: "table",
  ontologies: "ontology",
  metrics: "metric",
};

const mapApiEntityTypeToFlowType = (
  apiType?: string,
): KgFlowNodeType | undefined => {
  switch (apiType) {
    case "dataSource":
      return "dataSource";
    case "schema":
      return "table";
    case "ontology":
      return "ontology";
    case "metric":
      return "metric";
    case "report":
      return "report";
    case "story":
      return "dataStory";
    default:
      return undefined;
  }
};

const isRefValue = (
  value: KgTableOverviewField["value"],
): value is KgTableOverviewRefValue =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const refIconToLogoUrl = (icon?: string): string | undefined => {
  if (!icon) return undefined;
  if (icon.includes("/") || icon.includes(".")) return icon;
  return resolveConnectorIconUrl({ catalogKey: icon });
};

const mapRefToSourceRef = (
  ref: KgTableOverviewRefValue,
): KgInspectorTableSourceRef => ({
  name: ref.name,
  logoUrl: refIconToLogoUrl(ref.icon),
  externalUrl: ref.externalUrl,
  href: ref.href,
});

const mapTableOverviewField = (
  field: KgTableOverviewField,
): KgInspectorTableOverviewFieldView => {
  if (field.valueType === "ref" && isRefValue(field.value)) {
    return {
      key: field.key,
      label: field.label,
      valueType: field.valueType,
      value: mapRefToSourceRef(field.value),
    };
  }

  if (field.valueType === "list" && Array.isArray(field.value)) {
    return {
      key: field.key,
      label: field.label,
      valueType: field.valueType,
      value: field.value,
    };
  }

  return {
    key: field.key,
    label: field.label,
    valueType: field.valueType,
    value: String(field.value),
  };
};

const mapPreviewRow = (row: KgTablePreviewRow) => ({
  id: row.id,
  name: row.name,
  role: row.badge.kind as KgTableKeyType,
});

export const mapOverviewToView = (
  response: KgNodeOverviewResponse,
): KgInspectorOverviewView => ({
  externalUrl: response.externalUrl,
  fields: response.fields.map(
    (field): KgInspectorOverviewFieldView => ({
      key: field.key,
      label: field.label,
      valueType: field.valueType,
      value: field.value,
    }),
  ),
});

export const mapTableOverviewToView = (
  response: KgTableOverviewResponse,
): KgInspectorTableOverviewView => ({
  externalUrl: response.externalUrl,
  fields: response.fields.map(mapTableOverviewField),
  tablePreview: {
    label: response.tablePreview.label,
    rows: response.tablePreview.rows.map(mapPreviewRow),
    showAllLabel: response.tablePreview.showAll?.label,
    total: response.tablePreview.total || response?.tablePreview?.rows?.length,
  },
});

export const mapTablesResponse = (
  response: KgDataSourceTablesResponse,
): KgInspectorTableRow[] =>
  response.rows.map((row) => {
    const link = toInspectorNavLink(
      normalizeDataSourceTableRow(row),
      "table",
    );
    return { ...link, primaryKey: row.primaryKey };
  });

export const mapReportsResponse = (
  response: KgDataSourceReportsResponse,
): KgInspectorReportRow[] =>
  response.rows.map((row) => ({
    title: row.title,
    connectedOn: row.connectedOn,
    isLinked: true,
    canvasNodeId: toReportCanvasNodeId(row.id),
  }));

export const mapUsedInResponse = (
  response: KgDataSourceUsedInResponse,
): KgInspectorUsedInSection[] =>
  (response.sections ?? [])
    .filter((section) => section.items.length > 0)
    .map((section) => ({
      id: usedInSectionId(section.key),
      label: section.label,
      nodeType: USED_IN_SECTION_NODE_TYPE[section.key],
      items: section.items.map((item) =>
        toInspectorNavLink(
          normalizeUsedInItem(item),
          USED_IN_SECTION_NODE_TYPE[section.key],
        ),
      ),
    }));

export const mapTraceResponse = (
  response: KgReportTraceResponse,
): KgInspectorUsedInSection[] =>
  response.sections
    .filter((section) => section.items.length > 0)
    .map((section) => ({
      id: section.key,
      label: section.label,
      nodeType: TRACE_SECTION_NODE_TYPE[section.key],
      items: section.items.map((item) =>
        toInspectorNavLink(
          normalizeUsedInItem(item),
          TRACE_SECTION_NODE_TYPE[section.key],
        ),
      ),
    }));

/**
 * "Fetched on" label. The live API sends ISO 8601; mock fixtures already send a
 * display label ("1 hour ago"), which is passed through untouched.
 */
export const toReportFetchedOnLabel = (
  value: string | undefined,
): string | undefined =>
  value && !Number.isNaN(Date.parse(value)) ? formatRelativeTime(value) : value;

export const mapReportOverviewToView = (
  response: KgReportOverviewResponse,
): KgInspectorReportOverviewView => ({
  sourceType: response.sourceType,
  externalUrl: response.externalUrl,
  description: response.description,
  source: response.source
    ? {
        name: response.source.name,
        logoUrl: response.source.logoUrl,
        externalUrl: response.source.externalUrl,
      }
    : undefined,
  reportType: response.reportType,
  fetchedOn: toReportFetchedOnLabel(response.fetchedOn),
  updatedOn: response.updatedOn,
  updatedBy: response.updatedBy,
});

export const mapReportActivityToView = (
  response: KgReportActivityResponse,
): KgInspectorReportActivityRow[] =>
  response.versions.map((row) => ({
    id: row.id,
    version: row.version,
    lastUpdated: row.lastUpdated,
    updatedBy: row.updatedBy,
  }));

export const mapDataStoryOverviewToView = (
  response: KgDataStoryOverviewResponse,
): KgInspectorDataStoryOverviewView => ({
  externalUrl: response.externalUrl,
  summary: response.summary,
  owner: response.owner,
  sharedWith: response.sharedWith,
  artifacts: response.artifacts.map((row) => ({
    id: row.id,
    format: row.format,
    label: row.label,
    publishedOn: row.publishedOn,
  })),
});

export const mapDataStoryActivityToView = (
  response: KgDataStoryActivityResponse,
): KgInspectorReportActivityRow[] =>
  response.versions.map((row) => ({
    id: row.id,
    version: row.version,
    lastUpdated: row.lastUpdated,
    updatedBy: row.updatedBy,
    context: row.context,
  }));

export const mapArtifactOverviewToView = (
  response: KgArtifactOverviewResponse,
): KgInspectorArtifactOverviewView => ({
  externalUrl: response.externalUrl,
  format: response.format,
  sharedWith: response.sharedWith,
  updatedOn: response.updatedOn,
});

export const mapArtifactActivityToView = (
  response: KgArtifactActivityResponse,
): KgInspectorArtifactActivityView => ({
  summary: response.summary,
  events: response.events.map((row) => ({
    id: row.id,
    type: row.type,
    timestamp: row.timestamp,
    published: row.published,
  })),
});

export const mapRelationshipsResponse = (
  response: KgSchemaRelationshipsResponse,
): KgInspectorRelationshipRow[] =>
  response.rows.map((row) => ({
    id: row.id,
    columnName: row.column,
    relationType: row.relationLabel,
    tableLink: toInspectorNavLink(
      normalizeSchemaRelationshipRow(row),
      "table",
    ),
  }));

export const mapMetricsResponse = (
  response: KgSchemaMetricsResponse,
): KgInspectorMetricRow[] =>
  response.items.map((item) =>
    toInspectorNavLink(normalizeSchemaMetricItem(item), "metric"),
  );

const isOntologyRefValue = (
  value: KgOntologyOverviewField["value"],
): value is KgOntologyOverviewRefValue[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  typeof value[0] === "object" &&
  value[0] !== null &&
  "name" in value[0];

const mapOntologyRefToDataSource = (
  ref: KgOntologyOverviewRefValue,
): KgInspectorOntologyOverviewView["dataSources"][number] => {
  const link = toInspectorNavLink(normalizeOntologyOverviewRef(ref));
  return {
    ...link,
    dataSourceName: ref.secondary?.name ?? "",
  };
};

const mapOntologyDataSourcesFromFields = (
  fields: KgOntologyOverviewField[] | undefined,
): KgInspectorOntologyOverviewView["dataSources"] => {
  if (!fields?.length) return [];
  const dataSourcesField = fields.find((field) => field.key === "dataSources");
  if (!dataSourcesField || !isOntologyRefValue(dataSourcesField.value)) {
    return [];
  }
  return dataSourcesField.value.map(mapOntologyRefToDataSource);
};

const fieldsFindText = (
  fields: KgOntologyOverviewField[] | undefined,
  key: string,
): string | undefined => {
  const field = fields?.find((f) => f.key === key);
  return field && typeof field.value === "string" ? field.value : undefined;
};

export const mapOntologyOverviewToView = (
  response: KgOntologyOverviewResponse,
): KgInspectorOntologyOverviewView => {
  const description =
    response.description ?? fieldsFindText(response.fields, "description") ?? "";

  const dataSources =
    response.dataSources?.map((item) => {
      const link = toInspectorNavLink(
        normalizeOntologyDataSourceItem(item),
        "table",
      );
      return { ...link, dataSourceName: item.dataSourceName };
    }) ?? mapOntologyDataSourcesFromFields(response.fields);

  return {
    externalUrl: isExternalHttpUrl(response.externalUrl)
      ? response.externalUrl
      : undefined,
    description,
    dataSources,
  };
};

/** Data model Overview reuses the ontology overview view (Description block only). */
export const mapDataModelOverviewToView = (
  response: KgDataModelOverviewResponse,
): KgInspectorOntologyOverviewView => ({
  externalUrl: isExternalHttpUrl(response.externalUrl)
    ? response.externalUrl
    : undefined,
  description: response.description ?? "",
  dataSources: [],
});

/** Data model Relationships → two-line pair rows (Figma 2745-38725). */
export const mapDataModelRelationshipsResponse = (
  response: KgDataModelRelationshipsResponse,
): KgInspectorRelationshipPairRow[] =>
  response.rows.map((row) => ({
    id: row.id,
    from: { name: row.fromTable, column: row.fromColumn ?? undefined },
    relationType: row.relationType,
    cardinality: row.cardinality ?? undefined,
    to: { name: row.toTable, column: row.toColumn ?? undefined },
  }));

const mapOntologyLinkEntityRef = (
  ref: KgOntologyLinkEntityRef,
): ReturnType<typeof toInspectorNavLink> =>
  toInspectorNavLink({
    id: ref.id,
    name: ref.name,
    type: mapApiEntityTypeToFlowType(ref.type),
    href: ref.href,
    externalUrl: ref.externalUrl,
  });

const mapOntologyLinkRow = (
  row: KgOntologyLinkRow,
  responseSource?: KgOntologyLinkEntityRef,
): KgInspectorOntologyLinkRow => {
  const targetRef =
    typeof row.target === "object" && row.target !== null ? row.target : null;
  const sourceName =
    row.source ?? (responseSource ? responseSource.name : "");
  const targetName = targetRef
    ? targetRef.name
    : typeof row.target === "string"
      ? row.target
      : "";

  const sourceLink = responseSource
    ? mapOntologyLinkEntityRef(responseSource)
    : isInspectorFocusHref(row.sourceHref)
      ? toInspectorNavLink({
          id: row.id,
          name: sourceName,
          href: row.sourceHref,
        })
      : undefined;

  const targetLink = targetRef
    ? mapOntologyLinkEntityRef(targetRef)
    : isInspectorFocusHref(row.targetHref)
      ? toInspectorNavLink({
          id: row.id,
          name: targetName,
          href: row.targetHref,
        })
      : undefined;

  return {
    id: row.id,
    source: sourceName,
    target: targetName,
    verb: row.verb,
    sourceLink,
    targetLink,
  };
};

export const mapOntologyLinksResponse = (
  response: KgOntologyLinksResponse,
): KgInspectorOntologyLinkRow[] =>
  (response.rows ?? []).map((row) => mapOntologyLinkRow(row, response.source));

export const mapOntologyRulesResponse = (
  response: KgOntologyRulesResponse,
): KgInspectorRuleSection[] =>
  (response.sections ?? []).map((section) => ({
    id: section.key,
    label: section.label,
    iconKey: section.key,
    items: section.items.map((item) =>
      toInspectorNavLink({
        id: item.id,
        name: item.name,
        externalUrl: item.externalUrl,
      }),
    ),
  }));

const mapOntologyReportRow = (
  row: KgOntologyReportRow | KgOntologyReportApiRow,
): KgInspectorOntologyReportRow => {
  const title =
    "title" in row && row.title
      ? row.title
      : "name" in row && row.name
        ? row.name
        : "";
  const dataSource =
    typeof row.dataSource === "string"
      ? row.dataSource
      : row.dataSource.name;
  const id = "id" in row ? row.id : undefined;
  return { title, dataSource, isLinked: true, canvasNodeId: id ? toReportCanvasNodeId(id) : undefined };
};

export const mapOntologyReportsResponse = (
  response: KgOntologyReportsResponse,
): KgInspectorOntologyReportRow[] =>
  (response.rows ?? []).map(mapOntologyReportRow);

export const mapMetricOverviewToView = (
  response: KgMetricOverviewResponse,
): KgInspectorMetricOverviewView => ({
  externalUrl: response.externalUrl ?? undefined,
  description: response.description ?? "",
  verificationStatus: response.verificationStatus ?? "unverified",
  verifiedBy: response.verifiedBy ?? undefined,
  lastUpdatedBy: response.lastUpdatedBy ?? undefined,
  updatedOn: response.updatedOn ?? undefined,
  tags: response.tags ?? [],
  ontologies: (response.ontologies ?? []).map((item) =>
    toInspectorNavLink(normalizeMetricOntologyLink(item), "ontology"),
  ),
});

export const mapMetricFormulaToView = (
  response: KgMetricFormulaResponse,
): KgInspectorMetricFormulaView => ({
  expression: response.expression ?? "",
  columns: (response.columns ?? []).map((column) => ({
    id: column.id,
    columnName: column.columnName,
    tableName: column.tableName,
    dataSourceName: column.dataSourceName,
  })),
});

export { isExternalHttpUrl } from "./kgInspectorNavLink";
